import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { useLocale, useTranslations } from 'next-intl';
import { useFormatDate } from 'hooks/useFormatDate';
import { Field } from 'elements/Field';
import {
  addDays,
  addMonths,
  firstDayOfWeek,
  isSameDay,
  isSameMonth,
  monthGrid,
  startOfDay,
  startOfMonth,
  withDay,
} from './calendarGrid';

interface Props {
  id: string;
  value: Date;
  // Called with the chosen day at the same time of day as `value`.
  onChange: (next: Date) => void;
  onBlur?: () => void;
  className?: string;
}

// A day picked from a month calendar that opens under the field. The field
// and the calendar follow the app's language: date order, month names,
// weekday initials and which day starts the week.
export const DatePicker: FC<Props> = ({
  id,
  value,
  onChange,
  onBlur,
  className,
}) => {
  const formatDate = useFormatDate();

  return (
    // The popover's own wrapper is what sits in the row, so the caller's
    // sizing goes on it.
    <Popover className={className}>
      {({ open, close }) => (
        <>
          <Field.Root focused={open} className={styles.root}>
            <PopoverButton id={id} onBlur={onBlur} className={styles.button}>
              <span>{formatDate(value, 'numeric')}</span>
              <FontAwesomeIcon
                icon={faCalendar}
                className={styles.icon}
                aria-hidden="true"
              />
            </PopoverButton>
          </Field.Root>

          <PopoverPanel
            anchor={{ to: 'bottom start', gap: '6px' }}
            className={styles.panel}
          >
            <Calendar
              value={value}
              onSelect={(day) => {
                onChange(withDay(value, day));
                close();
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export interface CalendarProps {
  value: Date;
  onSelect: (day: Date) => void;
}

// The month view inside the popover. Mounted fresh each time it opens, so
// it always starts on the chosen day. Also the calendar half of the
// DateTimePicker.
export const Calendar: FC<CalendarProps> = ({ value, onSelect }) => {
  const t = useTranslations();
  const formatDate = useFormatDate();
  const locale = useLocale();
  const gridRef = useRef<HTMLDivElement>(null);

  const today = startOfDay(new Date());
  const selected = startOfDay(value);
  const weekStartsOn = firstDayOfWeek(locale);

  // The day the keyboard is on. Moving it past the month's edge turns the
  // page.
  const [focusedDay, setFocusedDay] = useState(selected);
  const [month, setMonth] = useState(startOfMonth(selected));
  const days = monthGrid(month, weekStartsOn);

  // Keep the keyboard's day in view, and put focus on it. On open that is
  // the chosen day; afterwards whatever the arrows moved to.
  useEffect(() => {
    if (!isSameMonth(focusedDay, month)) {
      setMonth(startOfMonth(focusedDay));
      return;
    }
    gridRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-day="${focusedDay.toDateString()}"]`,
      )
      ?.focus();
  }, [focusedDay, month]);

  const moveFocus = (next: Date) => setFocusedDay(startOfDay(next));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focusedDay, -1),
      ArrowRight: () => addDays(focusedDay, 1),
      ArrowUp: () => addDays(focusedDay, -7),
      ArrowDown: () => addDays(focusedDay, 7),
      PageUp: () => addMonths(focusedDay, -1),
      PageDown: () => addMonths(focusedDay, 1),
      Home: () =>
        addDays(focusedDay, -((focusedDay.getDay() - weekStartsOn + 7) % 7)),
      End: () =>
        addDays(focusedDay, 6 - ((focusedDay.getDay() - weekStartsOn + 7) % 7)),
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      moveFocus(move());
    }
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <span className={styles.month}>
          {formatDate(month, 'monthAndYear')}
        </span>
        <div className={styles.nav}>
          <button
            type="button"
            onClick={() => moveFocus(addMonths(focusedDay, -1))}
            aria-label={t('datePicker.previousMonth')}
            className={styles.navBtn}
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => moveFocus(addMonths(focusedDay, 1))}
            aria-label={t('datePicker.nextMonth')}
            className={styles.navBtn}
          >
            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label={formatDate(month, 'monthAndYear')}
        onKeyDown={handleKeyDown}
        className={styles.grid}
      >
        <div role="row" className={styles.weekdays}>
          {days.slice(0, 7).map((day) => (
            <span
              key={day.getDay()}
              role="columnheader"
              aria-label={formatDate(day, { weekday: 'long' })}
              className={styles.weekday}
            >
              {formatDate(day, { weekday: 'narrow' })}
            </span>
          ))}
        </div>

        {Array.from({ length: 6 }, (_, week) => (
          <div key={week} role="row" className={styles.week}>
            {days.slice(week * 7, week * 7 + 7).map((day) => {
              const isSelected = isSameDay(day, selected);
              const isToday = isSameDay(day, today);
              const isFocused = isSameDay(day, focusedDay);
              return (
                <button
                  key={day.toDateString()}
                  type="button"
                  role="gridcell"
                  data-day={day.toDateString()}
                  tabIndex={isFocused ? 0 : -1}
                  aria-selected={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={formatDate(day, 'long')}
                  onClick={() => onSelect(day)}
                  onFocus={() => {
                    if (!isFocused) {
                      setFocusedDay(day);
                    }
                  }}
                  className={classNames(
                    styles.day,
                    !isSameMonth(day, month) && styles.outside,
                    isToday && !isSelected && styles.today,
                    isSelected && styles.selected,
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <span className={styles.hint}>{t('datePicker.hint')}</span>
        <button
          type="button"
          onClick={() => onSelect(today)}
          className={styles.todayBtn}
        >
          {t('datePicker.today')}
        </button>
      </div>
    </div>
  );
};

const styles = {
  root: 'px-0',
  button: classNames(
    'flex w-full cursor-pointer items-center justify-between gap-2.5 rounded-[10px] px-3.5 py-2',
    'text-left focus:outline-none',
  ),
  icon: 'shrink-0 text-[13px] text-ink-primary',
  panel: classNames(
    'z-[60] w-[300px] rounded-xl bg-surface-overlay shadow-lg ring-1 ring-hairline',
    'focus:outline-hidden',
  ),
  calendar: 'p-3',
  header: 'flex items-center justify-between px-1 pb-2',
  month: 'text-sm font-semibold capitalize text-ink-primary',
  nav: 'flex items-center gap-0.5',
  navBtn: classNames(
    'flex size-7 items-center justify-center rounded-lg text-[11px] text-ink-secondary',
    'hover:bg-ink-secondary/10 hover:text-ink-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
  grid: 'focus:outline-hidden',
  weekdays: 'grid grid-cols-7 pb-1',
  weekday: 'text-center text-[11px] font-medium text-ink-tertiary',
  week: 'grid grid-cols-7',
  day: classNames(
    'mx-auto flex size-9 items-center justify-center rounded-lg text-sm text-ink-primary',
    'hover:bg-ink-secondary/10 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
  outside: 'text-ink-tertiary',
  today: 'font-semibold text-accent-ink',
  selected: 'bg-accent-fill font-semibold text-white hover:bg-accent-fill',
  footer:
    'flex items-center justify-between border-t border-hairline px-1 pt-2',
  hint: 'whitespace-nowrap text-[11px] text-ink-tertiary',
  todayBtn: classNames(
    'rounded-lg px-2 py-1 text-xs font-semibold text-accent-ink',
    'hover:bg-accent-tint focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
};
